/**
 * Inline Spinner Component
 * Consistent spinner for buttons, inline actions, and small loading states
 * Replaces inconsistent CircularProgress usage
 */

import React from 'react';
import { CircularProgress } from '@mui/material';
import { loadingConfig } from './loadingConfig';

/**
 * Inline Spinner
 *
 * @param {Object} props
 * @param {string} props.size - 'xs' | 'sm' | 'md' | 'lg' | 'xl'
 * @param {string} props.color - 'primary' | 'secondary' | custom color
 * @param {string} props.label - Accessible label (defaults to Hebrew "טוען...")
 * @param {Object} props.sx - Additional MUI sx styles
 */
export default function InlineSpinner({
  size = 'md',
  color = 'primary',
  label,
  sx = {},
  ...props
}) {
  const { sizes, colors: configColors, a11y } = loadingConfig;

  // Get size from config
  const spinnerSize = sizes.spinner[size] || sizes.spinner.md;

  // Get color from config
  const spinnerColor =
    color === 'primary'
      ? configColors.spinner.primary
      : color === 'secondary'
      ? configColors.spinner.secondary
      : color;

  return (
    <CircularProgress
      size={spinnerSize}
      sx={{
        color: spinnerColor,
        ...sx,
      }}
      aria-label={label || a11y.labels.loading}
      role="status"
      {...props}
    />
  );
}

/**
 * Predefined spinner variants for common use cases
 */

/**
 * Button Spinner - For inside buttons
 */
export function ButtonSpinner({ sx = {}, ...props }) {
  return (
    <InlineSpinner
      size="sm"
      sx={{
        marginRight: 1,
        ...sx,
      }}
      {...props}
    />
  );
}

/**
 * Auto-Save Spinner - For SaveIndicator
 */
export function AutoSaveSpinner({ sx = {}, ...props }) {
  return (
    <InlineSpinner
      size="xs"
      label={loadingConfig.a11y.labels.saving}
      {...props}
    />
  );
}

/**
 * AI Analysis Spinner - For AI operations
 */
export function AIAnalysisSpinner({ sx = {}, ...props }) {
  return (
    <InlineSpinner
      size="sm"
      label={loadingConfig.a11y.labels.processing}
      sx={{
        marginRight: 1,
        ...sx,
      }}
      {...props}
    />
  );
}
